import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase.js'
import { motion } from 'framer-motion'
import MarketingLayout from '../layouts/MarketingLayout.jsx'
import SEOHead from '../components/SEOHead.jsx'
import CTAButton from '../components/CTAButton.jsx'
import FeatureBlock from '../components/FeatureBlock.jsx'
import FAQAccordion from '../components/FAQAccordion.jsx'
import PlaceholderImage from '../components/PlaceholderImage.jsx'
import Lightbox from '../components/Lightbox.jsx'
import { FEATURES_HOME, STEPS_HOME, FOR_WHO } from '../data/features.js'
import StatsRow from '../components/StatsRow.jsx'
import { FAQ_HOME } from '../data/faq.js'
import imgCreateQuiz from '../images/quiz-pedagogique-genere-par-ia.webp'
import imgLaunch    from '../images/quiz-multijoueurs-qrcode.webp'
import imgRace      from '../images/quiz-interactif-course-kart.webp'
import imgBonus      from '../images/quiz-kart-bonus.webp'
import imgCircuit    from '../images/quiz-course-telephone-et-pc.webp'
import imgAI         from '../images/quiz-genere-par-ia.webp'
import imgKahoot     from '../images/quiz-importer-kahoot-course.webp'
import imgRepetition from '../images/quiz-apprendre-par-repetition.webp'
import imgSynthese   from '../images/quiz-synthese-des-revisions.webp'

const STEP_IMAGES = {
  'quiz-pedagogique-genere-par-ia.webp': imgCreateQuiz,
  'quiz-multijoueurs-qrcode.webp':       imgLaunch,
  'quiz-interactif-course-kart.webp':    imgRace,
}

const FEATURE_IMAGES = {
  'quiz-kart-bonus.webp':                  imgBonus,
  'quiz-course-telephone-et-pc.webp':      imgCircuit,
  'quiz-genere-par-ia.webp':               imgAI,
  'quiz-importer-kahoot-course.webp':      imgKahoot,
  'quiz-apprendre-par-repetition.webp':    imgRepetition,
  'quiz-synthese-des-revisions.webp':      imgSynthese,
}

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'KartQuiz',
  applicationCategory: 'EducationalApplication',
  operatingSystem: 'Web browser',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
  description:
    'Plateforme française de quiz interactifs gamifiés sous forme de course de kart pour les enseignants, formateurs et animateurs.',
}

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_HOME.map(item => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a },
  })),
}

// ── Hero ─────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="bg-[#F8FAFC] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center gap-16">

          {/* Texte — 60 % */}
          <div className="flex-[3] space-y-6">
            <div className="inline-flex items-center gap-2 bg-kart-action/10 text-kart-action text-sm font-semibold px-3 py-1.5 rounded-full uppercase tracking-wide">
              🏁 Bêta gratuite
            </div>

            <h1 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl text-kart-night leading-tight">
              Révision fun en{' '}
              <span className="text-kart-blue">pôle position</span>
            </h1>

            <p className="text-slate-600 text-xl leading-relaxed max-w-xl">
              KartQuiz transforme tes quiz en courses de kart. Bonus, attaques, IA, synthèse pédagogique — tout ce qu'il faut pour qu'une heure de révision file en clin d'œil.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <CTAButton to="/login" size="lg">
                Créer un compte gratuit
              </CTAButton>
              <CTAButton variant="ghost" href="#comment-ca-marche" size="lg">
                Voir comment ça marche →
              </CTAButton>
            </div>

            <p className="text-slate-400 text-sm">
              Sans carte bancaire · Sans installation pour tes élèves · Quiz IA inclus
            </p>
          </div>

          {/* Vidéo démo — 50 % sur desktop */}
          <div className="flex-[3] w-full">
            <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src="https://www.youtube.com/embed/HjYr7_4V180?rel=0&modestbranding=1"
                title="Démo KartQuiz"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ── Comment ça marche ─────────────────────────────────────────────────────
function HowItWorks() {
  const [lightbox, setLightbox] = useState(null)

  return (
    <section id="comment-ca-marche" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-kart-night">
            De ton cours à la grille de départ en 2 minutes.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {STEPS_HOME.map((step, i) => (
            <div key={step.num} className="flex flex-col items-center text-center gap-5">
              <div className="relative w-full">
                <button
                  type="button"
                  onClick={() => setLightbox(step)}
                  aria-label={`Agrandir l'image : ${step.title}`}
                  className="w-full group block"
                >
                  <div className="relative overflow-hidden rounded-xl shadow-md">
                    <img
                      src={STEP_IMAGES[step.image]}
                      alt={step.imageAlt}
                      title={step.imageTitle}
                      width={step.width}
                      height={step.height}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      fetchPriority={i === 0 ? 'high' : 'auto'}
                      decoding="async"
                      className="w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors duration-300 flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-kart-night" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </button>
                <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-kart-action text-white font-display font-semibold text-lg flex items-center justify-center shadow-md pointer-events-none">
                  {step.num}
                </div>
              </div>
              <div className="text-4xl">{step.icon}</div>
              <div>
                <h3 className="font-display font-semibold text-xl text-kart-night mb-2">{step.title}</h3>
                <p className="text-slate-600">{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {lightbox && (
        <Lightbox
          src={STEP_IMAGES[lightbox.image]}
          alt={lightbox.imageAlt}
          title={lightbox.imageTitle}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  )
}

// ── Kahoot callout ────────────────────────────────────────────────────────
function KahootCallout() {
  return (
    <section className="py-12 bg-white border-y border-slate-100">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12">
          <div className="text-5xl shrink-0">📥</div>
          <div className="flex-1 text-center md:text-left">
            <p className="text-slate-400 text-sm font-semibold uppercase tracking-widest mb-1">Pour les utilisateurs Kahoot</p>
            <h2 className="font-display font-semibold text-2xl text-kart-night mb-2">
              Tes quiz Kahoot arrivent dans KartQuiz en 1 clic.
            </h2>
            <p className="text-slate-600">
              Colle l'URL d'un quiz Kahoot public — toutes les questions QCM sont importées automatiquement, prêtes à jouer avec les bonus et la vitesse adaptative. Des millions de quiz publics à ta disposition.
            </p>
          </div>
          <div className="shrink-0">
            <CTAButton to="/login" size="md">
              Essayer maintenant
            </CTAButton>
          </div>
        </div>
      </div>
    </section>
  )
}

// ── Features ─────────────────────────────────────────────────────────────
function Features() {
  return (
    <section className="py-20 md:py-28 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-kart-night">
            Plus qu'un quiz. Une vraie course.
          </h2>
        </div>

        <div className="space-y-24">
          {FEATURES_HOME.map((feature, i) => (
            <FeatureBlock key={feature.id} feature={feature} reversed={i % 2 !== 0} imgSrc={FEATURE_IMAGES[feature.image]} />
          ))}
        </div>
      </div>
    </section>
  )
}

// ── Pour qui ─────────────────────────────────────────────────────────────
function ForWho() {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-kart-night">
            Pensé pour les profs. Adopté par les classes.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {FOR_WHO.map(item => (
            <div
              key={item.level}
              className="bg-[#F8FAFC] rounded-2xl p-8 border border-slate-200 hover:border-kart-blue/30 hover:shadow-md transition-all"
            >
              <div className="text-4xl mb-4">{item.emoji}</div>
              <h3 className="font-display font-semibold text-xl text-kart-night mb-3">{item.level}</h3>
              <p className="text-slate-600 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── Demo ─────────────────────────────────────────────────────────────────
function Demo() {
  return (
    <section className="py-20 md:py-28 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-kart-night">
            Vois la course de l'intérieur.
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { f: 'demo-host-screen.png',     alt: 'Vue prof / grand écran KartQuiz',     w: 1200, h: 800 },
            { f: 'demo-mobile-player.png',   alt: 'Vue téléphone élève KartQuiz',         w: 600,  h: 1000 },
            { f: 'demo-bonus-animation.png', alt: "Animation d'un bonus en action",       w: 1000, h: 700 },
            { f: 'demo-final-stats.png',     alt: 'Synthèse de fin de partie KartQuiz',  w: 1000, h: 700 },
          ].map(img => (
            <PlaceholderImage
              key={img.f}
              filename={img.f}
              alt={img.alt}
              width={img.w}
              height={img.h}
              className="w-full"
            />
          ))}
        </div>
      </div>
    </section>
  )
}

// ── FAQ courte ────────────────────────────────────────────────────────────
function FAQShort() {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-kart-night">
            Les questions qu'on nous pose souvent.
          </h2>
        </div>

        <div className="bg-[#F8FAFC] rounded-2xl border border-slate-200 px-8 py-2">
          <FAQAccordion items={FAQ_HOME} />
        </div>

        <div className="text-center mt-8">
          <Link to="/faq" className="text-kart-blue font-medium hover:underline">
            Toutes les questions sur la FAQ →
          </Link>
        </div>
      </div>
    </section>
  )
}

// ── CTA final ─────────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="py-20 md:py-28 bg-kart-night text-white text-center">
      <div className="max-w-3xl mx-auto px-6 space-y-6">
        <h2 className="font-display font-semibold text-3xl md:text-4xl">
          Prêt à mettre ta classe sur la grille de départ ?
        </h2>
        <p className="text-slate-300 text-xl">
          Créer ton compte prend 30 secondes. La première course peut démarrer dans la foulée.
        </p>
        <CTAButton to="/login" size="lg">
          Créer un compte gratuit
        </CTAButton>
        <p className="text-slate-500 text-sm">
          Pas le temps maintenant ?{' '}
          <button
            onClick={() => window.location.assign(window.location.href)}
            className="underline hover:text-slate-400 transition"
            title="Ajoute cette page à tes favoris (Ctrl+D)"
          >
            Garde-nous en favori.
          </button>
        </p>
      </div>
    </section>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────
export default function MarketingHomePage() {
  const navigate = useNavigate()
  const [authChecked, setAuthChecked] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate('/home', { replace: true })
      } else {
        setAuthChecked(true)
      }
    })
  }, [navigate])

  if (!authChecked) return null

  return (
    <MarketingLayout>
      <SEOHead
        canonical="/"
        description="Transforme tes quiz en course de kart. Bonus, attaques, IA, synthèse pédagogique. Bêta gratuite, sans inscription côté élève."
        jsonLd={[homeJsonLd, faqJsonLd]}
      />
      <Hero />
      <HowItWorks />
      <KahootCallout />
      <Features />
      <ForWho />
      <StatsRow />
      <FAQShort />
      <FinalCTA />
    </MarketingLayout>
  )
}
